import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, ClipboardCheck, FileText, Globe, PenLine } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import dayjs, { fmtTime, initials } from '../lib/dates';
import { StudentPhoto } from '../components/Photos';
import { InstructorLesson } from '../types';
import LessonActions from '../components/LessonActions';
import AttendanceModal from '../components/AttendanceModal';
import PhoneLink from '../components/PhoneLink';
import { useUiStore } from '../store/ui';
import { STATE_CHIP, canSignAttendance, lessonState } from '../lib/lessonStatus';

function LessonItem({ lesson }: { lesson: InstructorLesson }) {
  const c = lesson.candidate;
  const openBilan = useUiStore((s) => s.openBilan);
  const [attendanceOpen, setAttendanceOpen] = useState(false);
  // Une leçon est « passée » (réalisée) une fois son horaire de fin dépassé :
  // on ne peut plus la reporter/annuler, on propose alors d'en faire le bilan.
  const isPast = dayjs().isAfter(dayjs(lesson.endsAt));
  const state = lessonState(lesson);
  const chip = STATE_CHIP[state];

  if (lesson.isOpenSlot) {
    return (
      <div className="rounded-2xl border-2 border-dashed border-green-400 bg-green-50/60 p-4 flex items-center gap-4">
        <div className="w-10 h-10 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0">
          <Globe size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-green-700">Ouvert à la réservation</div>
          <div className="text-xs text-green-600">
            {fmtTime(lesson.startsAt)} – {fmtTime(lesson.endsAt)} · visible dans l'espace élève
          </div>
        </div>
        <span className="text-xs text-green-600 font-medium flex-shrink-0">Non réservé</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center gap-4">
        <div className="relative overflow-hidden w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-indigo-600 text-white flex items-center justify-center text-xs font-semibold flex-shrink-0">
          {c ? initials(c.firstName, c.lastName) : '–'}
          <StudentPhoto candidateId={lesson.candidateId} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900">
            {c ? `${c.firstName} ${c.lastName}` : 'Élève'}
          </div>
          <div className="text-xs text-slate-500">
            {fmtTime(lesson.startsAt)} – {fmtTime(lesson.endsAt)} ·{' '}
            {lesson.lessonType ?? 'Conduite'} · {lesson.durationHours} h
          </div>
          {c && <PhoneLink phone={c.phoneNumber} name={`${c.firstName} ${c.lastName}`} />}
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {lesson.bookedOnline && (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-green-50 text-green-700 border border-green-200 text-xs font-semibold">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              Réservé en ligne
            </span>
          )}
          <span
            className={`px-2.5 py-1 rounded-full border text-xs font-semibold ${chip.className}`}
          >
            {chip.label}
          </span>
        </div>
      </div>
      {lesson.status !== 'cancelled' && (
        <div className="mt-3 flex flex-wrap items-center gap-2 justify-end">
          {/* Émargement : présence de l'élève (leçon commencée, pas encore honorée). */}
          {canSignAttendance(lesson) && (
            <button
              onClick={() => setAttendanceOpen(true)}
              className="px-4 py-2.5 border border-indigo-200 text-indigo-700 hover:bg-indigo-50 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <PenLine size={15} />
              Émargement
            </button>
          )}
          {isPast
            ? // Leçon passée : bilan (plus de report/rappel/annulation).
              c && (
                <button
                  onClick={() =>
                    openBilan({
                      candidateId: c.id,
                      candidateName: `${c.firstName} ${c.lastName}`,
                      lessonId: lesson.id,
                    })
                  }
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-lg shadow-sm transition-colors flex items-center gap-2"
                >
                  <ClipboardCheck size={15} />
                  {lesson.hasBilan ? 'Modifier le bilan' : 'Faire le bilan de leçon'}
                </button>
              )
            : // Leçon à venir : report / rappel / annulation.
              lesson.status === 'scheduled' && <LessonActions lesson={lesson} />}
        </div>
      )}
      {attendanceOpen && (
        <AttendanceModal lesson={lesson} onClose={() => setAttendanceOpen(false)} />
      )}
    </div>
  );
}

export default function PlanningView() {
  const [offset, setOffset] = useState(0);
  const weekRef = dayjs().add(offset, 'week').toISOString();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['planning', offset],
    queryFn: () => instructorApi.getPlanning(weekRef),
  });

  const accompaniment = useQuery({
    queryKey: ['exam-accompaniments', data?.weekStart, data?.weekEnd],
    queryFn: () => instructorApi.getAccompaniments(data!.days[0].date.slice(0,10), data!.days[data!.days.length-1].date.slice(0,10)),
    enabled: !!data?.days.length,
    refetchInterval: 30000,
  });
  const allLessons = data?.days.flatMap((d) => d.lessons) ?? [];
  const lessonCount = allLessons.filter((l) => !l.isOpenSlot).length;
  const openSlotCount = allLessons.filter((l) => l.isOpenSlot).length;

  const [downloadingSheet, setDownloadingSheet] = useState(false);
  const handleDownloadSheet = async () => {
    if (!data) return;
    setDownloadingSheet(true);
    try {
      const from = dayjs(data.weekStart).format('YYYY-MM-DD');
      const to = dayjs(data.weekEnd).format('YYYY-MM-DD');
      const blob = await instructorApi.downloadAttendanceSheet(from, to);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `emargement-${from}_${to}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      // Silencieux.
    } finally {
      setDownloadingSheet(false);
    }
  };

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-slate-900">Mon planning</h1>
          <p className="text-slate-600">Vos leçons et accompagnements aux examens.</p>
        </div>
        <button
          onClick={handleDownloadSheet}
          disabled={downloadingSheet || !data}
          className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 hover:bg-slate-50 disabled:opacity-50 text-sm font-medium rounded-lg transition-colors"
        >
          <FileText size={15} />
          {downloadingSheet ? '…' : "Feuille d'émargement (PDF)"}
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-slate-200 px-5 py-3.5 shadow-sm flex items-center justify-between">
        <button
          onClick={() => setOffset((o) => o - 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine précédente"
        >
          <ChevronLeft size={18} className="text-slate-600" />
        </button>
        <div className="text-center">
          <div className="text-sm font-semibold text-slate-900">
            {data
              ? `Semaine du ${dayjs(data.weekStart).format('D')} au ${dayjs(
                  data.weekEnd,
                ).format('D MMMM YYYY')}`
              : '…'}
          </div>
          <div className="text-xs text-slate-500">
            {lessonCount} leçon(s)
            {openSlotCount > 0 && ` · ${openSlotCount} créneau(x) ouvert(s)`}
          </div>
        </div>
        <button
          onClick={() => setOffset((o) => o + 1)}
          className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
          aria-label="Semaine suivante"
        >
          <ChevronRight size={18} className="text-slate-600" />
        </button>
      </div>

      {isLoading && <p className="text-slate-500">Chargement du planning…</p>}
      {isError && (
        <p className="text-red-600">
          Impossible de charger le planning : {(error as Error).message}
        </p>
      )}

      {accompaniment.isError && <p role="alert" className="text-red-600">Impossible de charger les accompagnements aux examens. <button onClick={() => void accompaniment.refetch()}>Réessayer</button></p>}
      {data && (
        <div className="space-y-6">
          {data.days.map((day) => (
            <div key={day.date}>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-sm font-semibold text-slate-900 capitalize">
                  {dayjs(day.date).format('dddd D MMMM')}
                </span>
                {dayjs(day.date).isSame(dayjs(), 'day') && (
                  <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[11px] font-semibold">
                    Aujourd'hui
                  </span>
                )}
              </div>
              <div className="space-y-3">
                {accompaniment.data?.filter(a => new Intl.DateTimeFormat('en-CA', { timeZone: a.timezone || 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(a.starts_at)) === day.date.slice(0,10)).map(a => <div key={a.id} className="rounded-2xl border border-indigo-200 bg-indigo-50 p-4">
                  <div className="font-semibold text-indigo-900">Accompagnement examen · {a.candidate_name}</div>
                  <div className="text-sm text-indigo-700">{new Date(a.starts_at).toLocaleTimeString('fr-FR', {timeZone: a.timezone || 'Europe/Paris', hour:'2-digit',minute:'2-digit'})} – {new Date(a.ends_at).toLocaleTimeString('fr-FR', {timeZone: a.timezone || 'Europe/Paris', hour:'2-digit',minute:'2-digit'})} · {a.center}</div>
                </div>)}
                {day.lessons.length === 0 ? (
                  <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500 text-center">
                    Aucune leçon planifiée
                  </div>
                ) : (
                  day.lessons.map((l) => <LessonItem key={l.id} lesson={l} />)
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
